import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import supabase from '../../../supabase';

const { FiPlus, FiCheckCircle, FiClock, FiAlertCircle, FiUser, FiCalendar, FiTrash2 } = FiIcons;

export default function RUHActionPlan({ reportId }) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newAction, setNewAction] = useState({
    type: 'immediate',
    description: '',
    responsible_person: '',
    deadline: ''
  });

  useEffect(() => {
    fetchActions();
  }, [reportId]);

  async function fetchActions() {
    try {
      const { data, error } = await supabase
        .from('ruh_actions_1788626000000')
        .select('*')
        .eq('report_id', reportId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setActions(data || []);
    } catch (err) {
      console.error('Error fetching RUH actions:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleAddAction(e) {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from('ruh_actions_1788626000000')
        .insert([{ ...newAction, report_id: reportId }]);

      if (error) throw error;
      setShowAdd(false);
      setNewAction({ type: 'immediate', description: '', responsible_person: '', deadline: '' });
      fetchActions();
    } catch (err) {
      alert('Kunne ikke lagre tiltak');
    }
  }

  async function toggleStatus(id, currentStatus) {
    const nextStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    try {
      const { error } = await supabase
        .from('ruh_actions_1788626000000')
        .update({ 
          status: nextStatus,
          completed_at: nextStatus === 'completed' ? new Date().toISOString() : null
        })
        .eq('id', id);

      if (error) throw error;
      fetchActions();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  }

  async function deleteAction(id) {
    if (!confirm('Er du sikker på at du vil fjerne dette tiltaket?')) return;
    try {
      const { error } = await supabase
        .from('ruh_actions_1788626000000')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchActions();
    } catch (err) {
      console.error('Error deleting action:', err);
    }
  }

  if (loading) return <div className="animate-pulse h-20 bg-gray-100 rounded-lg"></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <SafeIcon icon={FiCheckCircle} className="text-green-600" />
          Korrigerende tiltak (Tiltaksplan)
        </h3>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          <SafeIcon icon={FiPlus} />
          Legg til tiltak
        </button>
      </div>

      {showAdd && (
        <form onSubmit={handleAddAction} className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Type tiltak</label>
              <select
                value={newAction.type}
                onChange={(e) => setNewAction({ ...newAction, type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                required
              >
                <option value="immediate">Strakstiltak (Umiddelbart)</option>
                <option value="long_term">Forebyggende tiltak (Langsiktig)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Ansvarlig</label>
              <input
                type="text"
                value={newAction.responsible_person}
                onChange={(e) => setNewAction({ ...newAction, responsible_person: e.target.value })}
                placeholder="Navn på ansvarlig"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Beskrivelse av tiltak</label>
            <textarea
              value={newAction.description}
              onChange={(e) => setNewAction({ ...newAction, description: e.target.value })}
              placeholder="Hva skal gjøres?"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none h-20"
              required
            />
          </div>
          <div className="flex justify-between items-center">
            <div className="w-48">
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Frist</label>
              <input
                type="date"
                value={newAction.deadline}
                onChange={(e) => setNewAction({ ...newAction, deadline: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                required
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowAdd(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium"
              >
                Avbryt
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
              >
                Lagre tiltak
              </button>
            </div>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {actions.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
            <p className="text-gray-500 text-sm">Ingen korrigerende tiltak er registrert ennå.</p>
          </div>
        ) : (
          actions.map((action) => (
            <div 
              key={action.id}
              className={`p-4 rounded-xl border transition-all ${
                action.status === 'completed' 
                  ? 'bg-green-50 border-green-100 opacity-75' 
                  : 'bg-white border-gray-200 shadow-sm'
              }`}
            >
              <div className="flex items-start gap-4">
                <button
                  onClick={() => toggleStatus(action.id, action.status)}
                  className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                    action.status === 'completed'
                      ? 'bg-green-500 border-green-500 text-white'
                      : 'border-gray-300 hover:border-blue-500 text-transparent'
                  }`}
                >
                  <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />
                </button>
                
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
                      action.type === 'immediate' 
                        ? 'bg-orange-100 text-orange-700' 
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                      {action.type === 'immediate' ? 'Strakstiltak' : 'Forebyggende'}
                    </span>
                    {action.status === 'completed' && (
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-green-100 text-green-700">
                        Utført
                      </span>
                    )}
                  </div>
                  <p className={`text-sm font-medium ${action.status === 'completed' ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                    {action.description}
                  </p>
                  
                  <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-gray-500">
                    <div className="flex items-center gap-1.5">
                      <SafeIcon icon={FiUser} className="text-gray-400" />
                      {action.responsible_person}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <SafeIcon icon={FiCalendar} className="text-gray-400" />
                      Frist: {new Date(action.deadline).toLocaleDateString('no-NO')}
                    </div>
                    {action.completed_at && (
                      <div className="flex items-center gap-1.5 text-green-600">
                        <SafeIcon icon={FiCheckCircle} />
                        Fullført: {new Date(action.completed_at).toLocaleDateString('no-NO')}
                      </div>
                    )}
                  </div>
                </div>

                <button
                  onClick={() => deleteAction(action.id)}
                  className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <SafeIcon icon={FiTrash2} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}